/**
 * Helpers for enumerating the hour intervals an operator can target when
 * logging manual scrap, and for resolving an interval's existing scrap
 * count from the cached calculation-summary arrays.
 *
 * An interval can be a full clock-aligned hour (e.g. 13:00–14:00) or a
 * partial first/last hour when the shift starts/ends off-the-hour (e.g.
 * 06:30–07:00 for a shift starting at 06:30). Both are first-class
 * options. Each carries a `hourAlignedIntervalStartIso` so the caller can
 * look up the matching `oee_quality_calculation_summary` row (which is
 * always keyed on a clock-aligned hour) regardless of whether the
 * displayed range is full or partial.
 */

import {
    IQualityManualOutput,
    IQualityOEECalculationSummary,
} from "components/HMIUptimeDowntimeBar/HMIUptimeDowntimeBarInterfaces";
import {
    IManualQualityPending,
    IManualQualityPendingEntry,
} from "../contexts/HMICachedDataContext/HMICachedDataContextInterfaces";

const MS_IN_HOUR = 3_600_000;
/**
 * Minimum length for a partial first/last interval to be exposed in the
 * dropdown. Filters out zero-length artifacts that arise when the shift
 * boundary is essentially on the hour but jitters by a few ms (e.g., the
 * previous shift ends at 09:00:00.001 — without this, the dropdown would
 * show a useless "09:00 – 09:00" entry).
 */
const MIN_PARTIAL_INTERVAL_MS = 60_000;

export type IntervalKind = "current" | "previous";

export interface IShiftIntervalOption {
    /** Actual interval start (may be off-the-hour for the first partial). */
    intervalStartIso: string;
    /** Actual interval end (may be off-the-hour for the last partial). */
    intervalEndIso: string;
    /**
     * Start-of-hour the calc summary is keyed on (always clock-aligned).
     * Use this to read/write `oee_quality_calculation_summary` rows.
     */
    hourAlignedIntervalStartIso: string;
    /** Whether this interval falls in the current or previous shift. */
    kind: IntervalKind;
    /** True for the partial first/last interval of a shift. */
    isPartial: boolean;
}

const ceilToHour = (ms: number): number => Math.ceil(ms / MS_IN_HOUR) * MS_IN_HOUR;
const floorToHour = (ms: number): number => Math.floor(ms / MS_IN_HOUR) * MS_IN_HOUR;

/**
 * Enumerates the hour intervals fully or partially contained in the shift
 * window [shiftStart, shiftEnd].
 *
 * `cap` controls which intervals are considered "reached":
 * - When omitted, all intervals up to `shiftEnd` are included (used for
 *   the previous shift — every interval is by definition completed).
 * - When supplied (typically "now"), only intervals whose end ≤ `cap`
 *   are included by default (the in-progress interval is hidden).
 *
 * `includeInProgressHour` widens the cap by exactly one hour, which has
 * the effect of additionally including the **single** interval whose
 * start ≤ cap but whose end > cap — i.e., the currently-running
 * interval. The +1h extension is bounded: at `now=13:02`, the loop
 * stops at hour 13:00–14:00 (end 14:00 ≤ 14:02) and does not push
 * 14:00–15:00 (end 15:00 > 14:02). Same logic catches the in-progress
 * partial-first and partial-last cases.
 */
const enumerateShift = (
    shiftStartIso: string,
    shiftEndIso: string,
    kind: IntervalKind,
    cap?: Date,
    includeInProgressHour: boolean = false,
): IShiftIntervalOption[] => {
    const startMs = new Date(shiftStartIso).getTime();
    const endMs = new Date(shiftEndIso).getTime();
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
        return [];
    }
    const capMs = cap ? cap.getTime() : endMs;
    const effectiveCap = includeInProgressHour ? capMs + MS_IN_HOUR : capMs;
    const intervals: IShiftIntervalOption[] = [];

    const startHourFloor = floorToHour(startMs);
    const startHourCeil = ceilToHour(startMs);

    // Partial first hour (e.g. 06:30 → 07:00) when the shift doesn't start on the hour.
    if (startMs < startHourCeil) {
        const partialEndMs = Math.min(startHourCeil, endMs);
        if (partialEndMs <= effectiveCap && partialEndMs - startMs >= MIN_PARTIAL_INTERVAL_MS) {
            intervals.push({
                hourAlignedIntervalStartIso: new Date(startHourFloor).toISOString(),
                intervalEndIso: new Date(partialEndMs).toISOString(),
                intervalStartIso: new Date(startMs).toISOString(),
                isPartial: startHourFloor !== startMs,
                kind,
            });
        }
    }

    // Full clock-aligned hours fully inside the shift.
    const fullHoursStart = startMs <= startHourCeil ? startHourCeil : startMs;
    let cursor = fullHoursStart;
    while (cursor + MS_IN_HOUR <= endMs && cursor + MS_IN_HOUR <= effectiveCap) {
        intervals.push({
            hourAlignedIntervalStartIso: new Date(cursor).toISOString(),
            intervalEndIso: new Date(cursor + MS_IN_HOUR).toISOString(),
            intervalStartIso: new Date(cursor).toISOString(),
            isPartial: false,
            kind,
        });
        cursor += MS_IN_HOUR;
    }

    // Partial last hour (e.g. 14:00 → 14:30) when the shift doesn't end on
    // the hour AND the shift's actual end time has been reached (or the
    // partial is currently in progress, when `includeInProgressHour=true`).
    // The minimum-length filter skips degenerate ~zero-length intervals
    // from minor boundary jitter.
    if (cursor < endMs && endMs <= effectiveCap && endMs - cursor >= MIN_PARTIAL_INTERVAL_MS) {
        intervals.push({
            hourAlignedIntervalStartIso: new Date(cursor).toISOString(),
            intervalEndIso: new Date(endMs).toISOString(),
            intervalStartIso: new Date(cursor).toISOString(),
            isPartial: endMs !== cursor + MS_IN_HOUR,
            kind,
        });
    }
    return intervals;
};

/**
 * Returns every interval the operator may select: the previous shift in
 * full, plus current-shift intervals up to `now`. Result is ordered
 * chronologically (oldest previous-shift hour first, latest current-shift
 * hour last).
 *
 * By default the in-progress current-shift interval is excluded — the
 * "latest completed" semantics used by the flash hook. Pass
 * `includeInProgressHour=true` (modal) to additionally include the
 * currently-running interval as a selectable option.
 */
export const enumerateCompletedIntervals = (
    previousShiftStartIso: string | undefined,
    previousShiftEndIso: string | undefined,
    currentShiftStartIso: string | undefined,
    currentShiftEndIso: string | undefined,
    now: Date = new Date(),
    includeInProgressHour: boolean = false,
): IShiftIntervalOption[] => {
    const intervals: IShiftIntervalOption[] = [];
    if (previousShiftStartIso && previousShiftEndIso) {
        intervals.push(...enumerateShift(previousShiftStartIso, previousShiftEndIso, "previous"));
    }
    if (currentShiftStartIso && currentShiftEndIso) {
        intervals.push(
            ...enumerateShift(currentShiftStartIso, currentShiftEndIso, "current", now, includeInProgressHour),
        );
    }
    return intervals;
};

/**
 * Returns the most recently completed interval across both shifts, or
 * undefined when no interval has completed yet. Used by the flashing-button
 * hook to decide whether a new "needs entry" alert should fire.
 */
export const getLatestCompletedInterval = (
    previousShiftStartIso: string | undefined,
    previousShiftEndIso: string | undefined,
    currentShiftStartIso: string | undefined,
    currentShiftEndIso: string | undefined,
    now: Date = new Date(),
): IShiftIntervalOption | undefined => {
    const all = enumerateCompletedIntervals(
        previousShiftStartIso,
        previousShiftEndIso,
        currentShiftStartIso,
        currentShiftEndIso,
        now,
    );
    return all.length > 0 ? all[all.length - 1] : undefined;
};

/**
 * Reads the existing scrap count for a given hour-aligned interval from
 * the cached calculation-summary arrays. Returns 0 when no row exists.
 */
export const getScrapCountForInterval = (
    hourAlignedIntervalStartIso: string,
    kind: IntervalKind,
    currentShiftQualitySummary: IQualityOEECalculationSummary[] | undefined,
    previousShiftQualitySummary: IQualityOEECalculationSummary[] | undefined,
): number => {
    const summary = kind === "current" ? currentShiftQualitySummary : previousShiftQualitySummary;
    if (!summary) {
        return 0;
    }
    const target = new Date(hourAlignedIntervalStartIso).getTime();
    const match = summary.find((r) => new Date(r.interval_start).getTime() === target);
    return match ? match.scrap_count : 0;
};

/**
 * Sums scrap_count across the current shift summary, used to render the
 * "Shift total" counter. The number is whatever the local server has
 * persisted as of last sync; the modal computes a live preview by adding
 * the operator's pending edit on top.
 */
export const sumShiftScrapCount = (
    summary: IQualityOEECalculationSummary[] | undefined,
): number => (summary ?? []).reduce((sum, r) => sum + (r.scrap_count ?? 0), 0);

/**
 * Augmented summary row carrying a `_pending` flag so consumers can render
 * an indicator. The original `IQualityOEECalculationSummary` shape is
 * preserved so existing call sites keep working.
 */
export interface IQualityOEECalculationSummaryWithPending extends IQualityOEECalculationSummary {
    _pending?: boolean;
}

/**
 * Returns the pending entries for a given machine center.
 */
export const getPendingForMachineCenter = (
    pending: IManualQualityPending | undefined,
    machineCenterId: number | undefined,
): IManualQualityPendingEntry[] => {
    if (!pending || machineCenterId === undefined) {
        return [];
    }
    return Object.values(pending).filter((p) => p.machineCenterId === machineCenterId);
};

/**
 * Overlays pending manual-quality submissions on top of a shift's calc
 * summary array. For each pending entry that targets an interval in the
 * provided shift_date (when known), the matching summary row's
 * `scrap_count` is replaced with the pending value (and `total_count`
 * + `quality_ratio` are recomputed). Pending entries with no matching
 * server row become synthetic rows so the operator's optimistic value is
 * still visible.
 *
 * The original array is not mutated. Returned rows carry `_pending: true`
 * when they came from a pending entry.
 *
 * `shiftDate` (mill-local "YYYY-MM-DD HH:MM:SS") narrows the overlay to
 * pending entries whose hour falls within that shift. When omitted, all
 * pending entries for the MC are overlaid (used for shift-agnostic
 * consumers like the gauge).
 */
export const applyPendingOverlay = (
    summary: IQualityOEECalculationSummary[] | undefined,
    pending: IManualQualityPending | undefined,
    machineCenterId: number | undefined,
    shiftStartIso?: string,
    shiftEndIso?: string,
): IQualityOEECalculationSummaryWithPending[] => {
    const base: IQualityOEECalculationSummaryWithPending[] = (summary ?? []).map((r) => ({ ...r }));
    const pendingForMc = getPendingForMachineCenter(pending, machineCenterId);
    if (pendingForMc.length === 0) {
        return base;
    }

    const shiftStartMs = shiftStartIso ? new Date(shiftStartIso).getTime() : undefined;
    const shiftEndMs = shiftEndIso ? new Date(shiftEndIso).getTime() : undefined;
    const inShift = (intervalIso: string): boolean => {
        if (shiftStartMs === undefined || shiftEndMs === undefined) {
            return true;
        }
        const t = new Date(intervalIso).getTime();
        return t >= shiftStartMs && t < shiftEndMs;
    };

    for (const entry of pendingForMc) {
        if (!inShift(entry.hourAlignedIntervalStartIso)) {
            continue;
        }
        const targetMs = new Date(entry.hourAlignedIntervalStartIso).getTime();
        const idx = base.findIndex((r) => new Date(r.interval_start).getTime() === targetMs);
        if (idx >= 0) {
            const existing = base[idx];
            const newScrap = entry.value;
            const newTotal = existing.good_count + newScrap;
            const newRatio = newTotal === 0 ? null : existing.good_count / newTotal;
            base[idx] = {
                ...existing,
                _pending: true,
                quality_ratio: existing.quality_ratio === null ? null : newRatio,
                scrap_count: newScrap,
                total_count: newTotal,
            };
        } else {
            // No server row yet — synthesize one so the operator's
            // optimistic value still surfaces. good_count is unknown
            // (treat as 0); the next sync will provide the real one.
            base.push({
                _pending: true,
                good_count: 0,
                interval_start: entry.hourAlignedIntervalStartIso,
                machine_center_id: entry.machineCenterId,
                product_id: null,
                quality_calculation_method_id: 0,
                quality_ratio: entry.value === 0 ? null : 0,
                scrap_count: entry.value,
                shift_date: "",
                shift_day_id: 0,
                total_count: entry.value,
            });
        }
    }
    base.sort((a, b) => a.interval_start.localeCompare(b.interval_start));
    return base;
};

// ─────────────────────────────────────────────────────────────────────────────
// Raw manual-output helpers
//
// Parallel to the calc-summary helpers above, but operating on the raw
// manual-output rows (`IQualityManualOutput`: `interval_start` + `value`)
// rather than the derived calc summary (`scrap_count` + `good_count` + …).
// The modal reads these — the raw outputs are the definitive record of what
// the operator logged, and survive a local-server restart via warm-load.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Parses an `interval_start` string to epoch ms, tolerating both formats the
 * value arrives in: ISO-8601 (`"…T…Z"`, from the local-server) and Postgres
 * timestamp text (`"YYYY-MM-DD HH:MM:SS"`, from the cloud endpoint's
 * `created_at::text`). The space-separated form is treated as UTC. All
 * `interval_start` matching in the manual-output helpers — and the
 * pending-clear gate in `setParsedData` — must go through this so cloud- and
 * local-server-sourced rows compare uniformly.
 */
export const parseIntervalStartMs = (raw: string): number =>
    new Date(raw.includes("T") ? raw : raw.replace(" ", "T") + "Z").getTime();

/**
 * Reads the existing scrap value for a given hour-aligned interval from the
 * raw manual-output arrays. Returns 0 when no row exists. Mirrors
 * {@link getScrapCountForInterval} but reads the definitive raw outputs
 * (`value`) rather than the derived calc summary (`scrap_count`).
 */
export const getManualScrapForInterval = (
    hourAlignedIntervalStartIso: string,
    kind: IntervalKind,
    currentShiftQualityManualScrap: IQualityManualOutput[] | undefined,
    previousShiftQualityManualScrap: IQualityManualOutput[] | undefined,
): number => {
    const rows = kind === "current" ? currentShiftQualityManualScrap : previousShiftQualityManualScrap;
    if (!rows) {
        return 0;
    }
    const target = parseIntervalStartMs(hourAlignedIntervalStartIso);
    const match = rows.find((r) => parseIntervalStartMs(r.interval_start) === target);
    return match ? match.value : 0;
};

/**
 * Sums `value` across the manual-output rows, for the "Shift total" counter.
 * Parallels {@link sumShiftScrapCount} but over the raw manual outputs.
 */
export const sumShiftManualScrap = (manual: IQualityManualOutput[] | undefined): number =>
    (manual ?? []).reduce((sum, r) => sum + (r.value ?? 0), 0);

/**
 * A raw manual-output row augmented with a `_pending` flag so consumers can
 * render an optimistic indicator.
 */
export interface IQualityManualOutputWithPending extends IQualityManualOutput {
    _pending?: boolean;
}

/**
 * Overlays pending manual-quality submissions on top of a shift's raw
 * manual-output array. For each in-shift pending entry, the matching row's
 * `value` is replaced (and `_pending: true` set); pending entries with no
 * matching server row become synthetic rows so the operator's optimistic
 * value still surfaces. The original array is not mutated.
 *
 * Parallels {@link applyPendingOverlay} but for the raw-output shape — there
 * is no `total_count` / `quality_ratio` to recompute.
 */
export const applyManualScrapPendingOverlay = (
    manual: IQualityManualOutput[] | undefined,
    pending: IManualQualityPending | undefined,
    machineCenterId: number | undefined,
    shiftStartIso?: string,
    shiftEndIso?: string,
): IQualityManualOutputWithPending[] => {
    const base: IQualityManualOutputWithPending[] = (manual ?? []).map((r) => ({ ...r }));
    const pendingForMc = getPendingForMachineCenter(pending, machineCenterId);
    if (pendingForMc.length === 0) {
        return base;
    }

    const shiftStartMs = shiftStartIso ? parseIntervalStartMs(shiftStartIso) : undefined;
    const shiftEndMs = shiftEndIso ? parseIntervalStartMs(shiftEndIso) : undefined;
    const inShift = (intervalIso: string): boolean => {
        if (shiftStartMs === undefined || shiftEndMs === undefined) {
            return true;
        }
        const t = parseIntervalStartMs(intervalIso);
        return t >= shiftStartMs && t < shiftEndMs;
    };

    for (const entry of pendingForMc) {
        if (!inShift(entry.hourAlignedIntervalStartIso)) {
            continue;
        }
        const targetMs = parseIntervalStartMs(entry.hourAlignedIntervalStartIso);
        const idx = base.findIndex((r) => parseIntervalStartMs(r.interval_start) === targetMs);
        if (idx >= 0) {
            base[idx] = {
                ...base[idx],
                _pending: true,
                value: entry.value,
            };
        } else {
            base.push({
                _pending: true,
                interval_start: entry.hourAlignedIntervalStartIso,
                shift_date: "",
                value: entry.value,
            });
        }
    }
    base.sort((a, b) => parseIntervalStartMs(a.interval_start) - parseIntervalStartMs(b.interval_start));
    return base;
};
